import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Drawer, Select, Space, Spin, Switch, Tag, Tooltip, message } from "antd";
import { ApprovalSetup, ApprovalSource, dailyApprovalApis, normaliseSetup } from "../../../api/DailyApprovals";

// Who approves this project's site days.
//
// The chain normally comes from the org's approval hierarchy in Masters, keyed
// by ROLE and however many levels deep that is. This drawer shows it read-only
// and explains where it came from. A project that needs different people turns
// on the override and names them; clearing the override hands the project back
// to the org's hierarchy rather than switching approval off.
//
// A level the master defines but nobody on the project can fill is shown as
// unresolved rather than hidden — a day cannot be submitted into it, and the
// fix (add a user with that role, or override) belongs to a human.

const SOURCE_NOTE: Record<ApprovalSource, { label: string; colour: string; note: string }> = {
  PROJECT: {
    label: "This project's own approvers",
    colour: "purple",
    note: "These people were named for this project and override the org's hierarchy.",
  },
  SITE: {
    label: "Org hierarchy — this site",
    colour: "blue",
    note: "From Masters › Approval Hierarchy, the rule set for this project's site.",
  },
  ORG: {
    label: "Org hierarchy",
    colour: "blue",
    note: "From Masters › Approval Hierarchy, the org-wide rule. No site-specific rule is set.",
  },
  AUTO: {
    label: "Approval off — set to Auto",
    colour: "orange",
    note: "The hierarchy in Masters is set to Auto, so days are only locked on submit, never routed.",
  },
  NONE: {
    label: "Approval off",
    colour: "default",
    note: "No hierarchy is set in Masters and this project names no approvers, so submitting a day only locks it.",
  },
};

const ApprovalSetupDrawer: React.FC<{
  open: boolean;
  projectId: number | string;
  onClose: () => void;
  onSaved?: () => void;
}> = ({ open, projectId, onClose, onSaved }) => {
  const [setup, setSetup] = useState<ApprovalSetup | null>(null);
  const [override, setOverride] = useState(false);
  const [picks, setPicks] = useState<Record<number, number[]>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    dailyApprovalApis
      .getSetup(projectId)
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          message.error(res?.message || "Could not load the approval setup");
          return;
        }
        const next = normaliseSetup(res.data);
        setSetup(next);
        setOverride(next.overrideActive);
        const seeded: Record<number, number[]> = {};
        next.levels.forEach((level) => {
          seeded[level.level] = level.approvers.map((approver) => approver.userId);
        });
        setPicks(seeded);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const options = useMemo(
    () =>
      (setup?.projectUsers ?? []).map((user) => ({
        value: user.userId,
        label: user.projectUserType ? `${user.userName} · ${user.projectUserType}` : user.userName,
      })),
    [setup],
  );

  // While overriding, the ladder is as deep as the master's (so a project can
  // mirror it) and always offers one more rung than is filled.
  const editLevels = useMemo(() => {
    const filled = Object.keys(picks)
      .map(Number)
      .filter((level) => (picks[level] ?? []).length);
    const depth = Math.max(setup?.levelCount ?? 0, filled.length, 1);
    return Array.from({ length: Math.min(depth + 1, 10) }, (_, index) => index + 1);
  }, [picks, setup]);

  const save = async () => {
    setSaving(true);
    try {
      const levels = override
        ? editLevels.map((level) => ({ level, userIds: picks[level] ?? [] }))
        : [];
      const res = await dailyApprovalApis.saveSetup({ projectId: Number(projectId), levels });
      if (res?.status !== true) {
        message.error(res?.message || "Could not save the approval setup");
        return;
      }
      message.success(res.data?.message || "Approval setup saved");
      onSaved?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const source = setup ? SOURCE_NOTE[setup.source] ?? SOURCE_NOTE.NONE : SOURCE_NOTE.NONE;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Approval setup"
      width={520}
      extra={
        <Button type="primary" onClick={save} loading={saving} disabled={loading}>
          Save
        </Button>
      }
    >
      {loading || !setup ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message={
              <span>
                {source.label} <Tag color={source.colour}>{setup.source}</Tag>
              </span>
            }
            description={
              <>
                {source.note}
                <div style={{ marginTop: 6 }}>
                  Any one approver of the current level can approve or reject. Whoever submitted a day
                  cannot approve it, and org admins can always act. Changes here apply to days
                  submitted from now on — a day already under review keeps the chain it was submitted
                  into.
                </div>
              </>
            }
          />

          {setup.warnings.map((warning) => (
            <Alert key={warning} type="warning" showIcon message={warning} />
          ))}

          {!options.length && (
            <Alert type="warning" showIcon message="This project has no users yet. Add them under Project Users first." />
          )}

          <div>
            <label className="pms-appr-label" htmlFor="appr-override">
              Override for this project
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Switch id="appr-override" checked={override} onChange={setOverride} />
              <span className="pms-appr-hint" style={{ margin: 0 }}>
                {override
                  ? "This project uses the people chosen below."
                  : "This project follows the org's hierarchy in Masters."}
              </span>
            </div>
          </div>

          {!override && (
            <div>
              {setup.levels.length === 0 ? (
                <div className="pms-appr-hint">
                  No levels to show. Set a hierarchy in Masters › Approval Hierarchy, or turn on the
                  override above.
                </div>
              ) : (
                setup.levels.map((level) => (
                  <div key={level.level} style={{ marginBottom: 12 }}>
                    <label className="pms-appr-label">
                      Level {level.level}
                      {level.roleName ? ` · ${level.roleName}` : level.levelName ? ` · ${level.levelName}` : ""}
                    </label>
                    {level.unresolved ? (
                      <Tag color="red">No one on this project holds this role</Tag>
                    ) : (
                      <Space size={[6, 6]} wrap>
                        {level.approvers.map((approver) => (
                          <Tooltip
                            key={approver.userId}
                            title={
                              approver.canActOnModule
                                ? undefined
                                : "Holds the role but has no approve permission on Projects — they will be refused if they try to act."
                            }
                          >
                            <Tag color={approver.canActOnModule ? "blue" : "orange"}>
                              {approver.userName}
                              {approver.canActOnModule ? "" : " · cannot act"}
                            </Tag>
                          </Tooltip>
                        ))}
                      </Space>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {override &&
            editLevels.map((level) => {
              const previousFilled = level === 1 || (picks[level - 1] ?? []).length > 0;
              return (
                <div key={level}>
                  <label className="pms-appr-label" htmlFor={`appr-level-${level}`}>
                    Level {level} approvers{level === 1 ? "" : " (optional)"}
                  </label>
                  <Select
                    id={`appr-level-${level}`}
                    mode="multiple"
                    value={picks[level] ?? []}
                    onChange={(value) => setPicks((prev) => ({ ...prev, [level]: value }))}
                    options={options}
                    disabled={!previousFilled}
                    placeholder={previousFilled ? "Choose project users" : `Choose level ${level - 1} first`}
                    style={{ width: "100%" }}
                    optionFilterProp="label"
                  />
                </div>
              );
            })}

          {override && (
            <div className="pms-appr-hint">
              A day climbs one level at a time: approved at level 1 it waits for level 2, and so on.
              Turning the override off, or clearing every level, hands this project back to the org's
              hierarchy.
            </div>
          )}
        </Space>
      )}
    </Drawer>
  );
};

export default ApprovalSetupDrawer;
